import { useEffect, useRef, useState } from 'react'

import { ReactComponent as CopyIcon } from 'shared/assets/images/copy.svg'
import { ReactComponent as DeleteIcon } from 'shared/assets/images/delete.svg'
import { ReactComponent as EditIcon } from 'shared/assets/images/edit.svg'
import { ReactComponent as ViewIcon } from 'shared/assets/images/view.svg'
import {
  PagePreview,
  PreviewsSource
} from 'shared/components/PageList/hooks/usePagePreviews'
import { fetchImageBlob } from 'shared/helpers/file'
import { LiveUser } from 'shared/types/global'
import { Skeleton } from 'shared/ui/Skeleton'

import { Container, Tool, Tools } from './Styles'
import BlockingUserOverlay from './components/BlockingUserOverlay'

type ContentPagePreviewProps = {
  preview: PagePreview
  isDragging: boolean
  disableHover?: boolean
  editingLiveUser?: LiveUser
  onEdit: () => void
  onCopy: () => void
  onView: () => void
  onDelete: () => void
}

const ContentPagePreview: React.FC<ContentPagePreviewProps> = ({
  preview,
  isDragging,
  disableHover = false,
  editingLiveUser,
  onEdit,
  onCopy,
  onView,
  onDelete
}) => {
  const TOOLS = [
    {
      icon: <EditIcon />,
      onClick: onEdit
    },
    {
      icon: <CopyIcon />,
      onClick: onCopy
    },
    {
      icon: <ViewIcon />,
      onClick: onView
    },
    {
      icon: <DeleteIcon />,
      onClick: onDelete
    }
  ]

  const prevPreviewUrl = useRef<string | null>(null)
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(
    null
  )
  const [isBlobLoading, setIsBlobLoading] = useState<boolean>(true)

  useEffect(() => {
    if (prevPreviewUrl.current === preview.url) {
      return
    }

    prevPreviewUrl.current = preview.url

    // If the preview is cached, it's already a blob url
    if (preview.source === PreviewsSource.cache) {
      setPreviewBlobUrl(preview.url)
      setIsBlobLoading(false)
      return
    }

    const fetchImage = async () => {
      if (!preview.url) {
        return
      }

      try {
        setIsBlobLoading(true)
        const blob = await fetchImageBlob(preview.url)
        const blobUrl = URL.createObjectURL(blob)

        if (!blobUrl) {
          return
        }

        setPreviewBlobUrl(blobUrl)
      } catch (error) {
        console.error('Error fetching image blob:', error)
      } finally {
        setIsBlobLoading(false)
      }
    }

    fetchImage()
  }, [preview])

  if (editingLiveUser) {
    return <BlockingUserOverlay editingLiveUser={editingLiveUser} />
  }

  return (
    <Container
      url={previewBlobUrl}
      isLoading={preview.isUrlLoading || isBlobLoading}
      isDragging={isDragging}
      disableHover={disableHover}
    >
      {(isBlobLoading || preview.isUrlLoading) && (
        <Skeleton width="100%" height="100%" />
      )}
      <Tools>
        {TOOLS.map((tool, i) => (
          <Tool key={i} onClick={tool.onClick}>
            {tool.icon}
          </Tool>
        ))}
      </Tools>
    </Container>
  )
}

export default ContentPagePreview
